import React, { useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import api from "../../../api";
import { formatMonthKey } from "../performance/performanceFormat";

// "Prepare evidence": adds up the month's records into the KPI's monthly
// entry (server-side, GET /analyst-work/evidence/preview) and submits it
// through the existing KPI evidence workflow. It counts toward the score only
// after the reviewer approves it.
const PrepareEvidenceModal = ({ kpi, month, onClose, onSubmitted }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Phase 65: a KPI that also needs schools / category / subcategories /
  // feedback recipients comes back as a DRAFT until those are filled in.
  const [draftNotice, setDraftNotice] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/analyst-work/evidence/preview", { params: { kpi_id: kpi.kpi_id, month } })
      .then((res) => { if (!cancelled) setPreview(res.data?.data || null); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.message || "Could not prepare the evidence."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kpi.kpi_id, month]);

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await api.post("/analyst-work/evidence/prepare", { kpi_id: kpi.kpi_id, month, submit: true });
      if (res?.data?.data?.evidence?.context_incomplete) setDraftNotice(true);
      else onSubmitted?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not submit the evidence.");
    } finally {
      setSaving(false);
    }
  };

  const isRatio = preview && preview.aggregate?.denominator !== null && preview.aggregate?.denominator !== undefined;

  return (
    <Modal show onHide={saving ? undefined : onClose} centered className="kp-modal">
      <Modal.Header closeButton={!saving}>
        <Modal.Title as="div">
          <div className="tlp-eyebrow">Prepare KPI evidence · {formatMonthKey(month)}</div>
          <div className="kp-modal-title">{kpi.kpi_name}</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="kp-center"><Spinner animation="border" size="sm" /> Adding up your records…</div>
        ) : preview ? (
          <>
            <div className="kw-preview">
              {isRatio ? (
                <>
                  <span className="kp-strong">{preview.value}%</span>
                  <span className="kp-sub">{preview.aggregate.numerator} of {preview.aggregate.denominator}</span>
                </>
              ) : (
                <span className="kp-strong">{preview.aggregate.count}</span>
              )}
              <span className="kp-sub">From {preview.aggregate.record_count} record{preview.aggregate.record_count === 1 ? "" : "s"} this month</span>
            </div>
            <p className="kp-sub mb-0">
              This becomes your {formatMonthKey(month)} entry for this KPI and is sent to your reviewer.
              It counts toward your score only after approval; the records are locked while it is under review.
            </p>
          </>
        ) : null}
        {draftNotice && (
          <div className="alert alert-info mt-3 mb-0">
            Saved as a draft — this KPI also needs schools, category, subcategories and feedback recipients.
            Open My Performance → Enter Monthly Data for {formatMonthKey(month)} to complete them and submit for review.
          </div>
        )}
        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="tlp-btn tlp-btn-ghost" onClick={draftNotice ? () => onSubmitted?.() : onClose} disabled={saving}>{draftNotice ? "Close" : "Cancel"}</button>
        {preview && !draftNotice && (
          <button type="button" className="tlp-btn tlp-btn-primary" onClick={submit} disabled={saving}>
            {saving ? <><Spinner animation="border" size="sm" /> Submitting…</> : "Submit for review"}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default PrepareEvidenceModal;
